import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TrendingUp, BarChart3, Target, Tag, Layers, Activity, FileText } from 'lucide-react';
import { parseDecimalString } from '../../shared/domain/decimal';
import { formatFinancialPrice } from '../../shared/domain/formatters';
import { apiClient } from '../../api/client';
import { CANONICAL_SPOT_SYMBOLS } from '../../shared/constants/watchlist';
import { CRYPTO_BILINGUAL } from '../../shared/constants/bilingualTitles';

export const CANONICAL_WEATHER_SYMBOLS = CANONICAL_SPOT_SYMBOLS;

export interface TimeframeConfigItem {
  tf: string;
  label: string;
  desc: string;
  tier: 'tactical' | 'primary' | 'strategic';
  tierLabel: string;
  tierColor: string;
  tierBg: string;
  tierBorder: string;
}

export const TIMEFRAME_CONFIG: TimeframeConfigItem[] = [
  {
    tf: '1m',
    label: '1m 极速微观',
    desc: '分时异动 · 假突破过滤',
    tier: 'tactical',
    tierLabel: '战术层',
    tierColor: 'var(--accent-cyan)',
    tierBg: 'transparent',
    tierBorder: 'var(--border-subtle)',
  },
  {
    tf: '5m',
    label: '5m 分时启动',
    desc: '分时启动 · 异动扳机',
    tier: 'tactical',
    tierLabel: '战术层',
    tierColor: 'var(--accent-cyan)',
    tierBg: 'transparent',
    tierBorder: 'var(--border-subtle)',
  },
  {
    tf: '15m',
    label: '15m 日内波段',
    desc: '日内主波段 · 顺势启动',
    tier: 'tactical',
    tierLabel: '战术层',
    tierColor: 'var(--accent-cyan)',
    tierBg: 'transparent',
    tierBorder: 'var(--border-subtle)',
  },
  {
    tf: '30m',
    label: '30m 次级主升',
    desc: '次级传动 · 结构破位',
    tier: 'primary',
    tierLabel: '主航道',
    tierColor: 'var(--accent-purple, var(--accent-primary))',
    tierBg: 'transparent',
    tierBorder: 'var(--border-subtle)',
  },
  {
    tf: '1h',
    label: '1h 核心主升',
    desc: '核心主航道 · 空间规划',
    tier: 'primary',
    tierLabel: '主航道',
    tierColor: 'var(--accent-purple, var(--accent-primary))',
    tierBg: 'transparent',
    tierBorder: 'var(--border-subtle)',
  },
  {
    tf: '4h',
    label: '4h 宏观趋势',
    desc: '宏观驱动 · 大方向过滤',
    tier: 'strategic',
    tierLabel: '战略底色',
    tierColor: 'var(--color-warning)',
    tierBg: 'transparent',
    tierBorder: 'var(--border-subtle)',
  },
  {
    tf: '1d',
    label: '1d 战略底色',
    desc: '重型齿轮 · 趋势惯性之锚',
    tier: 'strategic',
    tierLabel: '战略底色',
    tierColor: 'var(--color-warning)',
    tierBg: 'transparent',
    tierBorder: 'var(--border-subtle)',
  },
];

// Professional quantitative regime styles (Purged legacy weather cloud and lightning icons)
export interface RegimeVisualToken {
  code: string;
  name: string;
  direction: 'BULLISH' | 'BEARISH' | 'RANGE' | 'UNAVAILABLE';
  dirLabel: string;
  dirIndicator: string;
  cardBg: string;
  cardBorder: string;
  textColor: string;
  barColor: string;
  badgeBg: string;
  badgeBorder: string;
}

export const REGIME_VISUAL_MAP: Record<string, RegimeVisualToken> = {
  EXPLOSIVE_SURGE: {
    code: 'EXPLOSIVE_SURGE',
    name: '爆量主升',
    direction: 'BULLISH',
    dirLabel: '看涨 BULL',
    dirIndicator: '▲',
    cardBg: 'var(--bg-surface)',
    cardBorder: 'var(--color-bullish)',
    textColor: 'var(--color-bullish)',
    barColor: 'var(--color-bullish)',
    badgeBg: 'transparent',
    badgeBorder: 'var(--color-bullish)',
  },
  STEADY_GRINDING_UP: {
    code: 'STEADY_GRINDING_UP',
    name: '稳健推升',
    direction: 'BULLISH',
    dirLabel: '看涨 BULL',
    dirIndicator: '▲',
    cardBg: 'var(--bg-surface)',
    cardBorder: 'var(--border-card)',
    textColor: 'var(--color-bullish)',
    barColor: 'var(--color-bullish)',
    badgeBg: 'transparent',
    badgeBorder: 'var(--color-bullish)',
  },
  TIGHT_SQUEEZE: {
    code: 'TIGHT_SQUEEZE',
    name: '极致收敛',
    direction: 'RANGE',
    dirLabel: '震荡 RANGE',
    dirIndicator: '◼',
    cardBg: 'var(--bg-surface)',
    cardBorder: 'var(--color-warning)',
    textColor: 'var(--color-warning)',
    barColor: 'var(--color-warning)',
    badgeBg: 'transparent',
    badgeBorder: 'var(--color-warning)',
  },
  VOLATILE_RANGE: {
    code: 'VOLATILE_RANGE',
    name: '宽幅震荡',
    direction: 'RANGE',
    dirLabel: '震荡 RANGE',
    dirIndicator: '◼',
    cardBg: 'var(--bg-surface)',
    cardBorder: 'var(--border-subtle)',
    textColor: 'var(--text-secondary)',
    barColor: 'var(--text-secondary)',
    badgeBg: 'transparent',
    badgeBorder: 'var(--border-subtle)',
  },
  FLASH_BREAKDOWN: {
    code: 'FLASH_BREAKDOWN',
    name: '恐慌暴跌',
    direction: 'BEARISH',
    dirLabel: '看跌 BEAR',
    dirIndicator: '▼',
    cardBg: 'var(--bg-surface)',
    cardBorder: 'var(--color-bearish)',
    textColor: 'var(--color-bearish)',
    barColor: 'var(--color-bearish)',
    badgeBg: 'transparent',
    badgeBorder: 'var(--color-bearish)',
  },
  GRINDING_BLEED: {
    code: 'GRINDING_BLEED',
    name: '阴跌流血',
    direction: 'BEARISH',
    dirLabel: '看跌 BEAR',
    dirIndicator: '▼',
    cardBg: 'var(--bg-surface)',
    cardBorder: 'var(--border-card)',
    textColor: 'var(--color-bearish)',
    barColor: 'var(--color-bearish)',
    badgeBg: 'transparent',
    badgeBorder: 'var(--color-bearish)',
  },
  UNAVAILABLE: {
    code: 'UNAVAILABLE',
    name: '预热未就绪',
    direction: 'UNAVAILABLE',
    dirLabel: '预热 WARMUP',
    dirIndicator: '—',
    cardBg: 'var(--bg-surface)',
    cardBorder: 'var(--border-subtle)',
    textColor: 'var(--text-muted)',
    barColor: 'var(--text-muted)',
    badgeBg: 'transparent',
    badgeBorder: 'var(--border-subtle)',
  },
};

// Fallback resolver mapping legacy weather tokens to quant regimes if needed
export function resolveRegimeVisualToken(trendState?: string, weatherState?: string): RegimeVisualToken {
  if (trendState && REGIME_VISUAL_MAP[trendState]) {
    return REGIME_VISUAL_MAP[trendState];
  }
  const w = (weatherState || '').toUpperCase();
  if (w === 'SUNNY_TREND') return REGIME_VISUAL_MAP.STEADY_GRINDING_UP;
  if (w === 'STORM_BREAKDOWN') return REGIME_VISUAL_MAP.FLASH_BREAKDOWN;
  if (w === 'THUNDERSTORM_OVERHEAT') return REGIME_VISUAL_MAP.TIGHT_SQUEEZE;
  if (w === 'FOG_STAGNATION') return REGIME_VISUAL_MAP.VOLATILE_RANGE;
  return REGIME_VISUAL_MAP.UNAVAILABLE;
}

export interface CascadeVerdict {
  cascade_code: string;
  cascade_label: string;
  macro_alignment: string;
  transmission_direction: string;
  confidence_tier: string;
  rationale: string;
  leading_5m_state: string;
  core_15m_state: string;
  macro_4h_state: string;
  details: string[];
}

export interface DirectionalMetrics {
  symbol?: string;
  timeframe?: string;
  close_price?: string;
  roc_3?: string;
  volume_ratio?: string;
  ma20?: string;
  ma20_divergence_pct?: string;
  ma20_slope_pct?: string;
  atr20?: string;
  atr20_quantile?: string;
  breakout_high_10?: string;
  breakdown_low_10?: string;
  positive_bars_10?: number;
  negative_bars_10?: number;
}

export interface CellData {
  timeframe: string;
  role_name?: string;
  horizon_desc?: string;
  action?: string;
  action_short?: string;
  weather_state?: string;
  score_4d?: string | number;
  score?: string | number;
  mark_price?: string | number;
  bar_close_utc?: string;
  data_timestamp_utc?: string;
  // Stage 055 Additive Directional Fields
  trend_state?: string;
  state_code?: number;
  primary_direction?: 'BULLISH' | 'BEARISH' | 'RANGE' | 'UNAVAILABLE' | string;
  momentum_strength?: number;
  rationale_tags?: string[];
  rationale_details?: string[];
  warmup_ready?: boolean;
  metrics?: DirectionalMetrics;
}

export interface SymbolData {
  symbol: string;
  unified_mark_price?: string;
  summary_action?: string;
  gear_tag?: string;
  inertia_score?: number;
  verdict_title?: string;
  verdict_desc?: string;
  cascade_verdict?: CascadeVerdict;
  cells?: Record<string, CellData>;
}

export interface TrendWeatherEnvelope {
  schema_version?: string;
  generated_at?: string;
  bar_close_utc?: string;
  status?: string;
  macro_snapshot?: {
    overall_weather?: string;
    bullish_symbols_count?: number;
    bearish_symbols_count?: number;
    breadth_verdict?: string;
  };
  symbols?: SymbolData[];
}

export class VisibilityRequestCoordinator<T> {
  private inFlightId = 0;
  private activePromise: Promise<T | null> | null = null;
  private isHidden = false;
  private abortController: AbortController | null = null;

  public async execute(fetchFn: (signal?: AbortSignal) => Promise<T>, bypassCache = false): Promise<T | null> {
    if (typeof document !== 'undefined' && !document.hidden) {
      this.isHidden = false;
    }
    if (this.isHidden) {
      return null;
    }
    if (!bypassCache && this.activePromise) {
      return this.activePromise;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    const currentId = ++this.inFlightId;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    this.abortController = controller;

    const promise = (async () => {
      try {
        const res = await fetchFn(controller?.signal);
        if (currentId !== this.inFlightId || this.isHidden || controller?.signal.aborted) {
          return null;
        }
        return res;
      } catch (err: any) {
        if (currentId !== this.inFlightId || this.isHidden || controller?.signal.aborted || err?.name === 'AbortError') {
          return null;
        }
        throw err;
      } finally {
        if (currentId === this.inFlightId) {
          this.activePromise = null;
          if (this.abortController === controller) {
            this.abortController = null;
          }
        }
      }
    })();

    this.activePromise = promise;
    return promise;
  }

  public cancelInFlight(): void {
    this.inFlightId++;
    this.activePromise = null;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  public onHide(): void {
    this.isHidden = true;
    this.cancelInFlight();
  }

  public onResume(): void {
    if (!this.isHidden) {
      // Already resumed/visible; no-op to coalesce duplicate visibility events and protect active work
      return;
    }
    this.isHidden = false;
    this.cancelInFlight();
  }

  public isCurrentlyHidden(): boolean {
    return this.isHidden;
  }

  public getInFlightId(): number {
    return this.inFlightId;
  }

  public hasActivePromise(): boolean {
    return this.activePromise !== null;
  }

  public getAbortController(): AbortController | null {
    return this.abortController;
  }
}

export const TrendWeatherGrid: React.FC = () => {
  const [data, setData] = useState<TrendWeatherEnvelope | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDisconnected, setIsDisconnected] = useState<boolean>(false);
  const [selectedCell, setSelectedCell] = useState<{
    symbol: string;
    tf: string;
    cell: CellData;
    markPrice?: string;
    cascade?: CascadeVerdict;
  } | null>(null);

  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const coordinatorRef = useRef<VisibilityRequestCoordinator<TrendWeatherEnvelope>>(new VisibilityRequestCoordinator());

  const fetchData = useCallback(async (bypassCache = false) => {
    setIsLoading(true);
    try {
      const res = await coordinatorRef.current.execute(
        (signal) => apiClient.getTrendWeatherMatrix(bypassCache, { signal }) as Promise<TrendWeatherEnvelope>,
        bypassCache,
      );
      if (!res) return;
      setData(res);
      setIsDisconnected(false);
    } catch {
      if (!document.hidden) {
        setIsDisconnected(true);
      }
    } finally {
      if (!coordinatorRef.current.hasActivePromise()) {
        setIsLoading(false);
      }
    }
  }, []);

  // Stage 066: 30-second cadence + Page Visibility pause/resume
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let isSubscribed = true;

    const startTimer = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        if (!document.hidden && isSubscribed) {
          fetchData();
        }
      }, 30000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        coordinatorRef.current.onHide();
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        if (isSubscribed) {
          coordinatorRef.current.onResume();
          fetchData();
          startTimer();
        }
      }
    };

    fetchData();
    if (!document.hidden) {
      startTimer();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isSubscribed = false;
      coordinatorRef.current.onHide();
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData]);

  // Stage 066: 5-second cooldown timer for manual refresh
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const cdTimer = setInterval(() => {
      setCooldownRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(cdTimer);
  }, [cooldownRemaining]);

  const handleManualRefresh = useCallback(() => {
    if (cooldownRemaining > 0 || isLoading) return;
    fetchData(true);
    setCooldownRemaining(5);
  }, [cooldownRemaining, isLoading, fetchData]);

  const macro = data?.macro_snapshot;
  const symMap: Record<string, SymbolData> = {};
  if (data?.symbols) {
    data.symbols.forEach((s) => {
      symMap[s.symbol] = s;
    });
  }

  // Derive 5-state machine status: loading | fresh | stale | disconnected | empty
  const statusRaw = data?.status ? String(data.status).toUpperCase() : '';
  const statusBadgeText = isDisconnected
    ? 'DISCONNECTED'
    : isLoading && !data
    ? 'LOADING'
    : statusRaw || (data ? 'FRESH' : 'EMPTY');

  const statusBadgeClass = isDisconnected
    ? 'badge-bearish'
    : isLoading && !data
    ? 'badge-neutral'
    : statusBadgeText === 'STALE'
    ? 'badge-amber'
    : statusBadgeText === 'EMPTY'
    ? 'badge-neutral'
    : 'badge-bullish';

  // Quantitative Macro Direction
  const bullishCount = macro?.bullish_symbols_count ?? 0;
  const bearishCount = macro?.bearish_symbols_count ?? 0;
  const isMacroBull = bullishCount >= 4;
  const isMacroBear = bearishCount >= 4;
  const macroDirection = isMacroBull ? '看涨主升 (BULL)' : isMacroBear ? '破位防守 (BEAR)' : '震荡胶着 (RANGE)';
  const macroDirColor = isMacroBull ? 'var(--color-bullish)' : isMacroBear ? 'var(--color-bearish)' : 'var(--text-muted)';
  const macroDirIndicator = isMacroBull ? '▲' : isMacroBear ? '▼' : '◼';

  return (
    <div className="view-panel active" id="view-trend-weather" role="tabpanel">
      {/* View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <TrendingUp size={20} style={{ color: 'var(--color-bullish)' }} />
            <h2 className="bilingual-header-title-zh">{CRYPTO_BILINGUAL.weather.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en">{CRYPTO_BILINGUAL.weather.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            7 标的 × 7 周期量化态势矩阵 · 严格闭合烛线 W≥3s · 5m/15m/4h 级联传导研判 · 100% 纯 Decimal 算子 · 杜绝天气假象
          </p>
        </div>
      </div>

      {/* Quantitative Summary Strip */}
      <div className="indicator-resonance-card" id="tw-summary-strip">
        <div className="ind-stat-item">
          <span className="ind-stat-label">大盘宏观态势</span>
          <div className="ind-stat-val">
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 700,
                color: macroDirColor,
                background: 'transparent',
                border: `1px solid ${isMacroBull ? 'var(--color-bullish)' : isMacroBear ? 'var(--color-bearish)' : 'var(--border-subtle)'}`,
              }}
            >
              <span>{macroDirIndicator}</span>
              <span>{macroDirection}</span>
            </span>
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">全市场能量配比</span>
          <div className="ind-stat-val">
            <span style={{ color: 'var(--color-bullish)', fontWeight: 600 }}>
              看涨 {macro?.bullish_symbols_count !== undefined ? `${macro.bullish_symbols_count}/7` : '--/--'}
            </span>
            <span style={{ color: 'var(--text-muted)' }}> / </span>
            <span style={{ color: 'var(--color-bearish)', fontWeight: 600 }}>
              看跌 {macro?.bearish_symbols_count !== undefined ? `${macro.bearish_symbols_count}/7` : '--/--'}
            </span>
          </div>
        </div>

        <div className="ind-stat-item" style={{ flex: 1, minWidth: '240px' }}>
          <span className="ind-stat-label">量化研判速览</span>
          <div className="ind-stat-val" style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {macro?.breadth_verdict || (isLoading ? '正在获取量化研判...' : isDisconnected ? '离线 / 暂无量化研判' : '--')}
          </div>
        </div>

        <div className="ind-stat-item">
          <span className="ind-stat-label">闭合基准时间</span>
          <div className="ind-stat-val" style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
            {data?.bar_close_utc || (isLoading ? '加载中...' : '--')}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className={`badge ${statusBadgeClass}`} id="tw-cache-badge">
            物化缓存: {statusBadgeText}
          </span>
          <button
            className="toggle-btn"
            id="btn-refresh-weather"
            onClick={handleManualRefresh}
            disabled={isLoading || cooldownRemaining > 0}
            style={{
              background: 'var(--bg-surface-hover)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-primary)',
              cursor: isLoading || cooldownRemaining > 0 ? 'not-allowed' : 'pointer',
              padding: '4px 10px',
              fontSize: '11px',
              borderRadius: '4px',
              opacity: isLoading || cooldownRemaining > 0 ? 0.6 : 1,
            }}
          >
            {cooldownRemaining > 0 ? `🔄 冷却中 (${cooldownRemaining}s)` : '🔄 强制刷新'}
          </button>
        </div>
      </div>

      {/* 7x7 Trader Quantitative Heatmap Matrix */}
      <section className="tags-matrix-panel" style={{ padding: '14px 16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
            borderBottom: '1px solid var(--border-subtle)',
            paddingBottom: '8px',
          }}
        >
          <div className="bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <BarChart3 size={15} style={{ color: 'var(--accent-cyan)' }} />
              <h3 className="bilingual-card-title-zh" style={{ margin: 0 }}>
                7 标的 × 7 周期态势热力矩阵
              </h3>
              <span className="venue-pill venue-hyperliquid" style={{ fontSize: '10px' }}>
                闭合事件驱动 · 滞后 W≥3s · 纯 Decimal
              </span>
            </div>
            <span className="bilingual-card-subtitle-en">REGIME HEATMAP MATRIX</span>
          </div>
          <div style={{ fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
            <span>快照时间: </span>
            <span style={{ color: 'var(--accent-cyan)' }}>
              {data?.generated_at ? (data.generated_at.slice(11, 19) || data.generated_at) : '--:--:--'}
            </span>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table id="weather-matrix-table" className="weather-matrix-table">
            <thead>
              <tr>
                <th className="timeframe-col" style={{ color: 'var(--text-muted)', fontSize: '10px', textAlign: 'left', padding: '6px 8px' }}>
                  时间级别 \ 交易标的
                </th>
                {CANONICAL_WEATHER_SYMBOLS.map((sym) => {
                  const sData = symMap[sym];
                  const rawPrice = sData?.unified_mark_price;
                  const formattedPrice = rawPrice ? formatFinancialPrice(rawPrice, sym) : '--';
                  const cv = sData?.cascade_verdict;
                  const cvColor = cv?.transmission_direction === 'BULLISH'
                    ? 'var(--color-bullish)'
                    : cv?.transmission_direction === 'BEARISH'
                    ? 'var(--color-bearish)'
                    : 'var(--text-muted)';

                  return (
                    <th key={sym} className="symbol-col" style={{ textAlign: 'center', padding: '6px 4px' }}>
                      <div style={{ fontWeight: 700, fontSize: '11px', color: 'var(--text-primary)' }}>
                        {sym}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--accent-cyan)', marginTop: '1px', fontFamily: 'var(--font-mono)' }}>
                        {formattedPrice}
                      </div>
                      {cv && cv.cascade_code !== 'CASCADE_UNAVAILABLE' && (
                        <div
                          title={cv.rationale}
                          style={{
                            fontSize: '8.5px',
                            marginTop: '2px',
                            color: cvColor,
                            background: 'transparent',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            border: '1px solid var(--border-subtle)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {cv.cascade_label}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {TIMEFRAME_CONFIG.map((cfg) => {
                const tf = cfg.tf;
                const isTierBoundary = tf === '15m' || tf === '1h';
                const rowBorderBottom = isTierBoundary
                  ? '2px solid var(--border-card)'
                  : '1px solid var(--border-subtle)';

                return (
                  <tr key={tf} style={{ borderBottom: rowBorderBottom }}>
                    <td
                      className="timeframe-col"
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '11px',
                        whiteSpace: 'nowrap',
                        padding: '4px 6px',
                        background: 'var(--bg-surface)',
                        borderRadius: '5px',
                        borderRight: '1px solid var(--border-subtle)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                        <strong style={{ color: 'var(--text-primary)', fontSize: '10.5px' }}>{cfg.label}</strong>
                        <span
                          style={{
                            fontSize: '8.5px',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            background: cfg.tierBg,
                            border: `1px solid ${cfg.tierBorder}`,
                            color: cfg.tierColor,
                            fontWeight: 600,
                          }}
                        >
                          {cfg.tierLabel}
                        </span>
                      </div>
                      <div style={{ fontSize: '8.5px', color: 'var(--text-muted)', marginTop: '1px' }}>
                        {cfg.desc}
                      </div>
                    </td>

                    {CANONICAL_WEATHER_SYMBOLS.map((sym) => {
                      const sData = symMap[sym];
                      const cell = sData?.cells?.[tf];
                      const regToken = resolveRegimeVisualToken(cell?.trend_state, cell?.weather_state);
                      const momentum = cell?.momentum_strength ?? 0;
                      const isWarmedUp = cell?.warmup_ready ?? (cell?.trend_state ? cell.trend_state !== 'UNAVAILABLE' : true);
                      const tagCount = cell?.rationale_tags?.length ?? 0;

                      return (
                        <td
                          key={sym}
                          className="weather-symbol-cell"
                          style={{ padding: '2px 2px', verticalAlign: 'top', cursor: cell ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (!cell) return;
                            setSelectedCell({
                              symbol: sym,
                              tf,
                              cell,
                              markPrice: sData?.unified_mark_price,
                              cascade: sData?.cascade_verdict,
                            });
                          }}
                        >
                          <div
                            style={{
                              background: regToken.cardBg,
                              border: `1px solid ${regToken.cardBorder}`,
                              borderRadius: '5px',
                              padding: '4px 5px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '3px',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {/* Direction & State Pill */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  color: regToken.textColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <span>{regToken.dirIndicator}</span>
                                <span>{regToken.name}</span>
                              </span>
                              <span
                                style={{
                                  fontSize: '9px',
                                  padding: '1px 5px',
                                  borderRadius: '3px',
                                  background: regToken.badgeBg,
                                  border: `1px solid ${regToken.badgeBorder}`,
                                  color: regToken.textColor,
                                  fontWeight: 600,
                                }}
                              >
                                {isWarmedUp ? `${momentum}` : '预热'}
                              </span>
                            </div>

                            {/* Momentum Strength Bar */}
                            <div
                              title={`动量强度: ${momentum}/100`}
                              style={{
                                height: '4px',
                                borderRadius: '2px',
                                background: 'var(--border-subtle)',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: isWarmedUp ? `${momentum}%` : '0%',
                                  height: '100%',
                                  background: regToken.barColor,
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </div>

                            {/* Metrics snippet or tag pill */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '9px' }}>
                              <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                                {isWarmedUp ? regToken.dirLabel : '<120K'}
                              </span>
                              {tagCount > 0 && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', color: 'var(--accent-cyan)', fontSize: '9px' }}>
                                  <Tag size={9} />
                                  <span>{tagCount}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {/* Row 8: 跨周期级联研判 (Multi-Timeframe Cascade Verdict) */}
              <tr style={{ borderTop: '2px solid var(--border-subtle)', background: 'var(--bg-canvas)' }}>
                <td
                  className="timeframe-col"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    padding: '8px 10px',
                    background: 'var(--bg-surface)',
                    borderRight: '1px solid var(--border-subtle)',
                    borderRadius: '6px',
                  }}
                >
                  <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--accent-cyan)' }}>
                    <Target size={13} />
                    <span>跨周期级联研判</span>
                  </strong>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    5m/15m/4h 传导与共振
                  </div>
                </td>
                {CANONICAL_WEATHER_SYMBOLS.map((sym) => {
                  const sData = symMap[sym];
                  const cv = sData?.cascade_verdict;
                  const label = cv?.cascade_label || sData?.summary_action || '--';
                  const dir = cv?.transmission_direction;
                  const isBull = dir === 'BULLISH';
                  const isBear = dir === 'BEARISH';

                  const badgeBg = 'transparent';
                  const badgeBorder = isBull
                    ? 'var(--color-bullish)'
                    : isBear
                    ? 'var(--color-bearish)'
                    : 'var(--border-subtle)';
                  const badgeColor = isBull
                    ? 'var(--color-bullish)'
                    : isBear
                    ? 'var(--color-bearish)'
                    : 'var(--text-secondary)';

                  return (
                    <td
                      key={sym}
                      className="weather-symbol-cell"
                      style={{
                        padding: '6px 8px',
                        textAlign: 'center',
                        fontSize: '10px',
                        verticalAlign: 'middle',
                      }}
                    >
                      <span
                        title={cv?.rationale || ''}
                        style={{
                          display: 'inline-block',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          background: badgeBg,
                          border: `1px solid ${badgeBorder}`,
                          color: badgeColor,
                          fontSize: '10px',
                          fontWeight: 600,
                          maxWidth: '130px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {label}
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Expandable Fact Rationale Chain Modal (可展开事实依据链) */}
      {selectedCell && (
        <div
          className="modal-backdrop"
          onClick={() => setSelectedCell(null)}
        >
          <div
            className="glass-panel"
            style={{
              width: '580px',
              maxWidth: '94%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '22px',
              borderRadius: '12px',
              border: '1px solid var(--border-glass)',
              background: 'var(--bg-surface-elevated)',
              boxShadow: 'var(--shadow-drawer)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '14px',
                borderBottom: '1px solid var(--border-subtle)',
                paddingBottom: '10px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart3 size={15} style={{ color: 'var(--color-bullish)' }} />
                <span>
                  {selectedCell.symbol} · {selectedCell.tf.toUpperCase()} 量化态势与事实依据链
                </span>
              </div>
              <button
                onClick={() => setSelectedCell(null)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {(() => {
              const regToken = resolveRegimeVisualToken(selectedCell.cell.trend_state, selectedCell.cell.weather_state);
              const momentum = selectedCell.cell.momentum_strength ?? 0;
              const tags = selectedCell.cell.rationale_tags || [];
              const details = selectedCell.cell.rationale_details || [];
              const metrics = selectedCell.cell.metrics || {};
              const cascade = selectedCell.cascade;
              const rawPrice = selectedCell.markPrice ? parseDecimalString(selectedCell.markPrice) : '--';

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {/* Regime State Banner */}
                  <div
                    style={{
                      background: regToken.cardBg,
                      border: `1px solid ${regToken.cardBorder}`,
                      borderRadius: '8px',
                      padding: '12px 14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px' }}>{regToken.dirIndicator}</span>
                        <strong style={{ fontSize: '14px', color: regToken.textColor }}>{regToken.name}</strong>
                        <span
                          style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: regToken.badgeBg,
                            border: `1px solid ${regToken.badgeBorder}`,
                            color: regToken.textColor,
                            fontWeight: 700,
                          }}
                        >
                          {regToken.dirLabel}
                        </span>
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        标的标记价: <span style={{ color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>{rawPrice} USDT</span> · 闭合时间: <span style={{ fontFamily: 'var(--font-mono)' }}>{selectedCell.cell.bar_close_utc || '--'}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>动量强度</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, color: regToken.textColor, fontFamily: 'var(--font-mono)' }}>
                        {momentum}<span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>/100</span>
                      </div>
                    </div>
                  </div>

                  {/* Fact Tag Rationale Chain (事实依据链) */}
                  <div>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      <Tag size={12} />
                      <span>事实依据链</span>
                    </h4>
                    {tags.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {tags.map((tag) => (
                          <span
                            key={tag}
                            style={{
                              fontSize: '11px',
                              fontFamily: 'var(--font-mono)',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              background: 'var(--badge-info-bg)',
                              border: '1px solid var(--badge-info-border)',
                              color: 'var(--accent-cyan)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span>✓</span>
                            <span>{tag}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 8px', background: 'var(--bg-card)', borderRadius: '4px' }}>
                        当前状态符合判定规则，无离散异常事实标签触发。
                      </div>
                    )}
                  </div>

                  {/* Admitted Bar Quant Metrics Table */}
                  <div>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      <Layers size={12} />
                      <span>闭合烛线精确量化指标快照</span>
                    </h4>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '8px',
                        fontSize: '11px',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      <div style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>MA20 偏离度: </span>
                        <strong style={{ color: regToken.textColor }}>{metrics.ma20_divergence_pct ? `${metrics.ma20_divergence_pct}%` : '--'}</strong>
                      </div>
                      <div style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>MA20 斜率: </span>
                        <strong style={{ color: 'var(--text-primary)' }}>{metrics.ma20_slope_pct ? `${metrics.ma20_slope_pct}%` : '--'}</strong>
                      </div>
                      <div style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>ATR20 样本分位数: </span>
                        <strong style={{ color: 'var(--text-primary)' }}>{metrics.atr20_quantile ? `${metrics.atr20_quantile}` : '--'}</strong>
                      </div>
                      <div style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>3 根 K 棒变动率: </span>
                        <strong style={{ color: 'var(--text-primary)' }}>{metrics.roc_3 ? `${metrics.roc_3}%` : '--'}</strong>
                      </div>
                      <div style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>成交量比: </span>
                        <strong style={{ color: 'var(--text-primary)' }}>{metrics.volume_ratio ? `${metrics.volume_ratio}x` : '--'}</strong>
                      </div>
                      <div style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                        <span style={{ color: 'var(--text-muted)' }}>近 10 棒阳线数: </span>
                        <strong style={{ color: 'var(--text-primary)' }}>{metrics.positive_bars_10 !== undefined ? `${metrics.positive_bars_10}/10` : '--'}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Multi-Timeframe Cascade Diagnostic */}
                  {cascade && (
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '10px 12px' }}>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, color: 'var(--accent-cyan)', marginBottom: '6px' }}>
                        <Activity size={12} />
                        <span>跨周期级联联动诊断</span>
                      </h4>
                      <div style={{ fontSize: '11px', color: 'var(--text-primary)', marginBottom: '6px', fontWeight: 600 }}>
                        {cascade.cascade_label}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '6px' }}>
                        {cascade.rationale}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', fontSize: '10px', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                        <span>5m 启动: <strong style={{ color: 'var(--text-primary)' }}>{cascade.leading_5m_state}</strong></span>
                        <span>15m 主波段: <strong style={{ color: 'var(--text-primary)' }}>{cascade.core_15m_state}</strong></span>
                        <span>4h 宏观: <strong style={{ color: 'var(--text-primary)' }}>{cascade.macro_4h_state}</strong></span>
                      </div>
                    </div>
                  )}

                  {/* Audit Trail Details */}
                  {details.length > 0 && (
                    <div>
                      <h4 style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        <FileText size={12} />
                        <span>判据审计链</span>
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                        {details.map((d, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ color: 'var(--accent-cyan)' }}>•</span>
                            <span>{d}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Close button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                    <button
                      className="toggle-btn"
                      onClick={() => setSelectedCell(null)}
                      style={{
                        padding: '4px 16px',
                        fontSize: '11px',
                        background: 'var(--bg-surface-hover)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      关闭
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};
